import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { onAuthStateChange } from './firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase/config';
import { AuthProvider } from './contexts/AuthContext';
import { ListsProvider } from './contexts/ListsContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import ListDetail from './pages/ListDetail';
import PublicListPreview from './pages/PublicListPreview';
import CreateList from './pages/CreateList';
import EditList from './pages/EditList';
import LoadingSpinner from './components/LoadingSpinner';
import DocumentTitle from './components/DocumentTitle';
import MetaDescription from './components/MetaDescription';
import HtmlLang from './components/HtmlLang';
import ProtectedRoute from './components/ProtectedRoute';
import InstallPrompt from './components/InstallPrompt';
import './index.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (authUser) => {
      if (authUser) {
        // Load user preferences from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', authUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUser({
              ...authUser,
              preferences: userData.preferences || {}
            });
          } else {
            setUser(authUser);
          }
        } catch (error) {
          console.error('Error loading user preferences:', error);
          setUser(authUser);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider value={{ user, setUser }}>
          <ListsProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <HtmlLang />
          <DocumentTitle />
          <MetaDescription />
          <div className="min-h-screen bg-gray-50">
            <Navbar />
            <InstallPrompt />
            <main className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 lg:py-8 pt-14 sm:pt-16">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route 
                  path="/login" 
                  element={<Login />} 
                />
                <Route 
                  path="/register" 
                  element={<Register />} 
                />
                <Route 
                  path="/forgot-password" 
                  element={<ForgotPassword />} 
                />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/list/:id" 
                  element={<PublicListPreview />} 
                />
                <Route 
                  path="/list/:id/view" 
                  element={
                    <ProtectedRoute>
                      <ListDetail />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/create-list" 
                  element={
                    <ProtectedRoute>
                      <CreateList />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/edit-list/:id" 
                  element={
                    <ProtectedRoute>
                      <EditList />
                    </ProtectedRoute>
                  } 
                />
              </Routes>
            </main>
            <Toaster 
              position="top-right"
              containerStyle={{
                top: '100px',
                right: '20px',
              }}
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                  marginTop: '20px',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
            </Router>
          </ListsProvider>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
